import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewEncapsulation,
    effect,
    inject,
    input,
    signal,
    viewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { S25DatepickerModule } from "../../s25-datepicker/s25.datepicker.module";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { Table } from "../../s25-table/Table";
import { UserprefService } from "../../../services/userpref.service";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { DataIngestionService, IngestionWorkflowAuditRow } from "../../data-ingestion/data.ingestion.service";
import { IngestionFormComponent } from "../../data-ingestion/ingestion.form.component";
import { S25Util } from "../../../util/s25-util";
import { FlsStore } from "../../../stores/fls.store";
import { WorkflowService } from "../service/workflow.service";
import { AlertService } from "../../../services/alert.service";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";

@TypeManagerDecorator("workflow-submission-audit-page")
@Component({
    selector: "workflow-submission-audit-page",
    template: `
        @if (init()) {
            <div class="page-header">
                <h1>Submission Workflow Audit</h1>
                <div class="page-controls">
                    <label class="filter-control">
                        <span>From:</span>
                        <s25-datepicker [(value)]="fromDate" />
                    </label>
                    <label class="filter-control">
                        <span>To:</span>
                        <s25-datepicker [(value)]="toDate" />
                    </label>
                    <button class="aw-button aw-button--primary" (click)="refreshTable()">Search</button>
                </div>
            </div>

            @if (errorMessage()) {
                <div class="error-alert">
                    <div class="error-icon">⚠️</div>
                    <div class="error-text">{{ errorMessage() }}</div>
                </div>
            }

            <s25-ng-table
                #auditTable
                [caption]="'Submission workflow audit'"
                [unlimitedWidth]="true"
                [columnSortable]="true"
                [dataSource]="tableConfig"
                [hasTotalRowCount]="true"
                [hasRefresh]="true"
                [hasFilter]="true"
                [showHeaderWhenNoData]="true"
                [hasExpandableRows]="true"
                [hasExport]="true"
                [exportFilename]="'submission-workflow-audit.xlsx'"
                [exportSheetName]="'Submission workflow audit'"
            ></s25-ng-table>
        } @else {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        }
    `,
    styles: `
        .page-header {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            margin-bottom: 20px;
        }

        .page-controls {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            align-items: center;
        }

        .filter-control {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .error-alert {
            display: flex;
            align-items: center;
            background-color: #fee;
            border: 1px solid #fcc;
            border-radius: 4px;
            padding: 12px;
            margin-bottom: 20px;
        }

        .error-icon {
            margin-right: 8px;
        }

        .error-text {
            flex: 1;
        }

        :host ::ng-deep .expanded-content-wrapper {
            max-height: min(70vh, 720px);
            overflow: auto;
        }

        .audit-details {
            max-width: 280px;
            white-space: pre-wrap;
            word-break: break-word;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, S25TableModule, S25DatepickerModule, S25LoadingInlineModule],
})
export class WorkflowSubmissionAuditPageComponent implements OnInit {
    private flsStore = inject(FlsStore);
    readonly canReset = () => this.flsStore.hasFull("STUDENT_ENGAGEMENT");

    init = signal(false);
    dateFormat = "";
    errorMessage = signal<string>("");
    rows = signal<IngestionWorkflowAuditRow[]>([]);

    fromDate = signal<Date | null>(null);
    toDate = signal<Date | null>(null);

    tableConfig!: Table.DataSource;
    auditTable = viewChild("auditTable", { read: S25TableComponent });

    activeMode = input<string | null>(null);

    constructor() {
        let wasVisible = false;
        let hasInitialized = false;
        effect(() => {
            const isVisible = this.activeMode() === "workflowSubmissionAudit";
            if (isVisible && !wasVisible && hasInitialized) {
                this.refreshTable();
            }
            if (isVisible) {
                hasInitialized = true;
            }
            wasVisible = isVisible;
        });
    }

    async ngOnInit() {
        const [dateFormat] = await Promise.all([UserprefService.getS25DateTimeformat(), this.flsStore.ensureLoaded()]);
        this.dateFormat = dateFormat;

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const tomorrowStart = new Date(todayStart);
        tomorrowStart.setDate(tomorrowStart.getDate() + 1);
        this.fromDate.set(todayStart);
        this.toDate.set(tomorrowStart);

        this.tableConfig = {
            type: "unpaginated",
            columns: [
                { id: "workflowItemId", header: "Workflow Item ID", export: true },
                { id: "auditDate", header: "When", export: true },
                { id: "ingestionPointName", header: "Form", export: true },
                { id: "organizationDisplayName", header: "Organization", export: true },
                { id: "submissionId", header: "Submission", export: true },
                { id: "auditUser", header: "Who", export: true },
                { id: "auditType", header: "Type", export: true },
                { id: "workflowProcessDisplayName", header: "Process", export: true },
                { id: "stageDisplayName", header: "Stage", export: true },
                { id: "stateDisplayName", header: "State", export: true },
                { id: "description", header: "Summary", export: true },
                { id: "auditDetails", header: "Details", export: true },
                ...(this.canReset()
                    ? [
                          GenericTableButtonComponent.Column(
                              "Reset",
                              this.resetWorkflow.bind(this),
                              "danger--outline",
                              "min-content",
                              false,
                              "Reset Workflow",
                          ),
                      ]
                    : []),
            ],
            dataSource: this.getAuditHistoryData,
        };

        this.init.set(true);
    }

    getAuditHistoryData = async (): Promise<Table.DataSourceResponse> => {
        await this.loadAuditHistory();

        const out: Table.Row[] = [];
        for (const item of this.rows()) {
            const submissionIdUuid = S25Util.toStr(item.submissionIdUuid ?? undefined);
            const ingestionPointId = item.ingestionPointId;
            const canExpand = !!submissionIdUuid && !!ingestionPointId;

            const row: Table.Row = {
                id: item.workflowAuditId?.toString() ?? "",
                name: `${item.ingestionPointName ?? "—"} — ${item.auditType ?? ""}`,
                expandable: canExpand,
                cells: {
                    workflowItemId: {
                        text: S25Util.isDefined(item.workflowItemId) ? String(item.workflowItemId) : "—",
                        sortValue: S25Util.isDefined(item.workflowItemId)
                            ? String(item.workflowItemId).padStart(10, "0")
                            : "",
                    },
                    auditDate: {
                        text: this.formatDate(item.auditDate),
                        sortValue: item.auditDate || "",
                    },
                    ingestionPointName: { text: item.ingestionPointName || "—" },
                    organizationDisplayName: { text: item.organizationDisplayName || "—" },
                    submissionId: { text: submissionIdUuid || "—" },
                    auditUser: { text: item.auditUser || "" },
                    auditType: { text: item.auditType || "" },
                    workflowProcessDisplayName: { text: item.workflowProcessDisplayName || "—" },
                    stageDisplayName: { text: item.stageDisplayName || this.stageFallback(item) },
                    stateDisplayName: { text: item.stateDisplayName || this.stateFallback(item) },
                    description: { text: item.description || "—" },
                    auditDetails: { text: this.auditDetailText(item), className: "audit-details" },
                },
                data: item,
            };

            if (canExpand) {
                row.expandedContent = {
                    component: IngestionFormComponent,
                    inputs: {
                        submissionIdUuid: submissionIdUuid,
                        ingestionPointId: ingestionPointId,
                        readOnly: true,
                        editMode: false,
                    },
                };
            }

            out.push(row);
        }

        return { rows: out };
    };

    async loadAuditHistory() {
        const from = this.fromDate();
        const to = this.toDate();

        if (!from || !to) {
            this.errorMessage.set("Please select a date range");
            return;
        }

        const start = new Date(from);
        start.setHours(0, 0, 0, 0);
        const end = new Date(to);
        end.setHours(0, 0, 0, 0);

        if (start.getTime() >= end.getTime()) {
            this.errorMessage.set(
                "Start date must be before end date (end is exclusive, typically midnight the next day)",
            );
            return;
        }

        this.errorMessage.set("");
        try {
            const data = await DataIngestionService.getWorkflowAuditHistoryBySubmissionModDateRange(start, end);
            this.rows.set(
                data?.sort((a, b) => {
                    const dateA = a.auditDate ? new Date(a.auditDate).getTime() : 0;
                    const dateB = b.auditDate ? new Date(b.auditDate).getTime() : 0;
                    return dateB - dateA;
                }) || [],
            );
        } catch (error) {
            console.error("Failed to load submission workflow audit:", error);
            this.errorMessage.set("Failed to load submission workflow audit. Please try again.");
            this.rows.set([]);
        }
    }

    refreshTable() {
        this.auditTable()?.refresh(true, true);
    }

    resetWorkflow = async (row: Table.Row) => {
        const item = row.data as IngestionWorkflowAuditRow;
        if (!item.workflowItemTypeId || !item.workflowItemId) {
            S25Util.showError("Invalid workflow item");
            return;
        }

        if (
            !(await AlertService.confirm(
                "Are you sure you want to reset this workflow? This will delete all workflow progress and restart from the beginning using the current workflow definition.",
            ))
        ) {
            return;
        }

        try {
            await WorkflowService.resetWorkflow(item.workflowItemTypeId, item.workflowItemId);
            this.refreshTable();
        } catch (error) {
            console.error("Error resetting workflow:", error);
            S25Util.showError(error);
        }
    };

    private stageFallback(row: IngestionWorkflowAuditRow): string {
        return row.stageId != null ? String(row.stageId) : "—";
    }

    private stateFallback(row: IngestionWorkflowAuditRow): string {
        return row.stateId != null ? String(row.stateId) : "—";
    }

    private auditDetailText(row: IngestionWorkflowAuditRow): string {
        const parts: string[] = [];
        if (row.auditType === "STAGE_TRANSITION" && row.previousValue) {
            parts.push(`From stage: ${row.previousValue}`);
        }
        if (row.newValue) {
            parts.push(row.newValue);
        }
        return parts.length ? parts.join("\n") : "—";
    }

    private formatDate(dateString?: string) {
        if (!dateString) return "—";
        return S25Datefilter.transform(dateString, this.dateFormat) || dateString;
    }
}
